import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { UserProvider, useUser } from './context/UserContext';
import LoginScreen from './pages/LoginScreen';
import LORAppShell from './pages/lor/LORAppShell';
import HomeSelector, { LeagueSwitcher } from './components/HomeSelector';
import Standings from './pages/Standings';
import PowerRankings from './pages/PowerRankings';
import TradeLab from './pages/TradeLab';
import Transactions from './pages/Transactions';
import Waivers from './pages/Waivers';
import AgeCurve from './pages/AgeCurve';
import News from './pages/News';
import History from './pages/History';
import HeadToHead from './pages/HeadToHead';
import Discord from './pages/Discord';
import MyIntel from './pages/MyIntel';
import LegacyBoard from './pages/LegacyBoard';
import PatchNotes from './pages/PatchNotes';
import CommissionerHQ from './pages/CommissionerHQ';
import GameDay from './pages/GameDay';
import DynastyTeamBeat from './pages/DynastyTeamBeat';
import CommissionerWarRoom from './pages/CommissionerWarRoom';
import PickExchange from './pages/PickExchange';
import ADPPage from './pages/ADPPage';
import TGillRebuildPlan from './pages/TGillRebuildPlan';
import ManagerCommandCenter from './pages/ManagerCommandCenter';
import DynastyBylaws from './pages/DynastyBylaws';
import LeagueIntelDesk from './pages/LeagueIntelDesk';
import LeagueIntelAssistant from './components/LeagueIntelAssistant';
import SiteTelemetry from './components/SiteTelemetry';
import './App.css';
import Icon from './components/Icon';
import { resolveFranchiseTheme } from './utils/franchiseThemes';

const API = process.env.REACT_APP_API_URL || '';

// ── Navigation structure ──────────────────────────────────────────────────────
// Each path appears in exactly one group. memberOnly paths are hidden/skipped
// for guests and non-roster users. flagKey paths are hidden when that feature
// flag is disabled (flags come from /api/status → feature_flags).
const NAV_GROUPS = [
  {
    key: 'league',
    label: 'League',
    paths: [
      { label: 'Standings',      to: '/',         exact: true },
      { label: 'Power Rankings', to: '/power'                 },
      { label: 'History',        to: '/history'               },
      { label: 'Game Day',       to: '/gameday'               },
      { label: 'Chat',           to: '/discord'               },
    ],
  },
  {
    key: 'transactions',
    label: 'Transactions',
    paths: [
      { label: 'Trades',  to: '/transactions', flagKey: 'trade_receipts'              },
      { label: 'Waivers', to: '/waivers',       memberOnly: true, flagKey: 'waiver_wire' },
    ],
  },
  {
    key: 'intel',
    label: 'Intel',
    paths: [
      { label: 'My Intel',          to: '/my-intel',   memberOnly: true                      },
      { label: 'Team Beat', to: '/team-beat' },
      { label: 'League Reputation', to: '/legacy',     flagKey: 'league_reputation'           },
      { label: 'Rivalries',         to: '/h2h',        flagKey: 'rivalries'                   },
      { label: 'Age Curve',         to: '/age-curve'                                          },
      { label: 'News',              to: '/news'                                               },
    ],
  },
  {
    key: 'tools',
    label: 'Tools',
    paths: [
      { label: 'ADP',              to: '/adp'                                       },
      { label: 'Trade Evaluator', to: '/trades',         flagKey: 'trade_evaluator' },
      { label: 'Pick Exchange',   to: '/pick-exchange',  flagKey: 'pick_exchange'   },
      { label: 'Patch Notes',     to: '/patchnotes',     flagKey: 'patch_notes'     },
    ],
  },
  {
    key: 'bylaws',
    label: '📜 By-Laws',
    paths: [
      { label: 'By-Laws', to: '/bylaws' },
    ],
  },
];

// Commissioner group — never visible in public nav lists, only rendered when
// the logged-in user is the commissioner.
const COMMISSIONER_GROUP = {
  key: 'commissioner',
  label: 'Commissioner',
  commissioner: true,
  paths: [
    { label: 'Commissioner HQ', to: '/intel' },
    { label: '😈 War Room', to: '/war-room' },
  ],
};

// ── AppShell — lives inside <Router> so it can use routing hooks ──────────────
function AppShell({ status, posting, postMsg, onRefresh, onPostDiscord, onToggleTheme, theme }) {
  const { currentUser, logout } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const isCommissioner =
    currentUser?.display_name === 'PhillyxCommish' ||
    currentUser?.username === 'PhillyxCommish';
  const managerKeys = [currentUser?.display_name, currentUser?.username]
    .map(value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, ''));
  const privateIntelTabs = {
    tgill55: { label: 'Rebuild Room', to: '/rebuild-plan' },
    dominantnation: { label: 'Trade Governor', to: '/trade-governor' },
    eazypickens: { label: 'Three-Peat', to: '/three-peat' },
    jcole94: { label: 'Curse Breaker', to: '/curse-breaker' },
  };
  const privateIntelTab = privateIntelTabs[managerKeys.find(key => privateIntelTabs[key])];
  const isLeagueMember = !currentUser?.guest && currentUser?.roster_id;
  const franchiseTheme = resolveFranchiseTheme(currentUser);
  const avatarUrl = currentUser.avatar
    ? `https://sleepercdn.com/avatars/thumbs/${currentUser.avatar}`
    : null;

  const flags = status?.feature_flags || {};
  const flagVisible = (p) => !p.flagKey || flags[p.flagKey] !== false;

  const personalizedGroups = NAV_GROUPS.map(group => group.key === 'intel' && privateIntelTab
    ? { ...group, paths: [...group.paths, { ...privateIntelTab, memberOnly: true }] }
    : group);
  const allGroups = [...personalizedGroups, ...(isCommissioner ? [COMMISSIONER_GROUP] : [])];

  // Derive the active primary group from the current URL
  const activeGroup = allGroups.find(g =>
    g.paths.some(p => location.pathname === p.to)
  ) || null;

  // Sub-tabs: filtered by member access and feature flags
  const visibleSubs = activeGroup
    ? activeGroup.paths.filter(p => (!p.memberOnly || isLeagueMember) && flagVisible(p))
    : [];

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Never';

  // Clicking a primary tab navigates to the first accessible + flag-enabled sub-page
  const handlePrimaryClick = (group) => {
    const first = group.paths.find(p => (!p.memberOnly || isLeagueMember) && flagVisible(p));
    if (first) navigate(first.to);
  };

  return (
    <div
      className={`app${franchiseTheme ? ` franchise-theme franchise-theme--${franchiseTheme.id}` : ''}`}
      data-franchise-theme={franchiseTheme?.id || undefined}
      style={franchiseTheme ? { '--franchise-logo': `url(${franchiseTheme.logoUrl})` } : undefined}
    >
      <header className="header">
        {/* ── Brand bar: identity · league switcher ── */}
        <div className="header-brand">
          <div className="league-title">
            <div className="logo-mark"><Icon emoji="⚡" size={18} /></div>
            <div>
              <h1>League Intel</h1>
              <span className="subtitle">Dynasty · PPR · 10 Teams</span>
            </div>
          </div>
          <div className="header-brand-right">
            {franchiseTheme && (
              <div className="franchise-lockup" aria-label={`${franchiseTheme.label} fan theme`}>
                <img src={franchiseTheme.logoUrl} alt="" className="franchise-lockup-logo" />
                <span><small>Fan Mode</small>{franchiseTheme.label}</span>
              </div>
            )}
            <LeagueSwitcher current="dynasty" />
          </div>
        </div>

        {/* ── Ops bar: actions · status · user ── */}
        <div className="header-ops">
          <div className="header-ops-left">
            {isCommissioner && (
              <button className="btn btn-refresh" onClick={onRefresh}><Icon emoji="↻" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Refresh</button>
            )}
            {isCommissioner && status?.has_discord && (
              <button className="btn btn-discord" onClick={onPostDiscord} disabled={posting}>
                <Icon emoji={posting ? '⏳' : '📣'} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Post to Discord
              </button>
            )}
            <div className="last-updated">Updated {fmt(status?.last_updated)}</div>
            {(status?.sleeper?.stale || status?.sleeper?.degraded) && (
              <span className="post-msg" title={status?.sleeper?.last_refresh_error || status?.sleeper?.last_error || ''}>
                <Icon emoji="⚠️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Sleeper data may be stale
              </span>
            )}
            {postMsg && <span className="post-msg"><Icon emoji={postMsg.type === 'success' ? '✅' : '❌'} size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{postMsg.text}</span>}
          </div>
          <div className="header-ops-right">
            {franchiseTheme && (
              <span className="franchise-theme-badge" aria-label={`${franchiseTheme.label} theme`}>
                <span aria-hidden="true">{franchiseTheme.mark}</span>
                <span className="franchise-theme-label">{franchiseTheme.shortLabel}</span>
              </span>
            )}
            <button
              className="theme-toggle"
              onClick={onToggleTheme}
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
              aria-label="Toggle theme"
            >
              <Icon emoji={theme === 'light' ? '🌙' : '☀️'} size={18} />
            </button>
            <div className="user-pill">
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" className="user-avatar" />
                : <span className="user-avatar-placeholder"><Icon emoji="👤" size={20} /></span>}
              <span className="user-name">{currentUser.guest ? 'Guest' : currentUser.display_name}</span>
              <button className="user-switch" onClick={logout} title="Switch user"><Icon emoji="⇄" size={16} /></button>
            </div>
          </div>
        </div>

        {/* ── Primary navigation: 5 public groups + optional Commissioner ── */}
        <nav className="nav-primary">
          <div className="nav-primary-main">
            {personalizedGroups.filter(group =>
              group.paths.some(p => flagVisible(p))
            ).map(group => (
              <button
                key={group.key}
                data-telemetry={`nav:${group.key}`}
                className={`nav-primary-tab${activeGroup?.key === group.key ? ' active' : ''}`}
                onClick={() => handlePrimaryClick(group)}
              >
                {group.label}
              </button>
            ))}
          </div>
          {isCommissioner && (
            <button
              className={`nav-primary-tab commissioner${activeGroup?.key === 'commissioner' ? ' active' : ''}`}
              onClick={() => handlePrimaryClick(COMMISSIONER_GROUP)}
            >
              <Icon emoji="🔒" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Commissioner
            </button>
          )}
        </nav>

        {/* ── Sub navigation: shown only when the active group has 2+ visible tabs ── */}
        {visibleSubs.length > 1 && (
          <nav className="nav-sub">
            {visibleSubs.map(p => (
              <NavLink
                key={p.to}
                data-telemetry={`subnav:${p.to}`}
                to={p.to}
                end
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              >
                {p.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="main">
        <Routes>
          <Route path="/"            element={<Standings api={API} />} />
          <Route path="/gameday"     element={<GameDay api={API} />} />
          <Route path="/team-beat" element={<DynastyTeamBeat api={API} />} />
          <Route path="/war-room" element={<CommissionerWarRoom api={API} />} />
          <Route path="/power"       element={<PowerRankings api={API} />} />
          <Route path="/legacy"      element={<LegacyBoard api={API} />} />
          <Route path="/trades"      element={<TradeLab api={API} status={status} />} />
          <Route path="/transactions" element={<Transactions api={API} />} />
          <Route path="/waivers"     element={<Waivers api={API} />} />
          <Route path="/age-curve"   element={<AgeCurve api={API} />} />
          <Route path="/news"        element={<News api={API} />} />
          <Route path="/history"     element={<History api={API} />} />
          <Route path="/h2h"         element={<HeadToHead api={API} />} />
          <Route path="/discord"     element={<Discord api={API} />} />
          <Route path="/my-intel"    element={<MyIntel api={API} />} />
          <Route path="/league-desk" element={<LeagueIntelDesk api={API} league="dynasty" />} />
          <Route path="/adp"           element={<ADPPage api={API} />} />
          <Route path="/patchnotes"    element={<PatchNotes />} />
          <Route path="/pick-exchange" element={<PickExchange />} />
          <Route path="/intel"         element={<CommissionerHQ />} />
          <Route path="/rebuild-plan"  element={<TGillRebuildPlan api={API} />} />
          <Route path="/trade-governor" element={<ManagerCommandCenter api={API} room="trade-governor" owner="DominantNation" />} />
          <Route path="/three-peat" element={<ManagerCommandCenter api={API} room="three-peat" owner="EazyPickens" />} />
          <Route path="/curse-breaker" element={<ManagerCommandCenter api={API} room="curse-breaker" owner="JCole94" />} />
          <Route path="/bylaws"        element={<DynastyBylaws />} />
        </Routes>
      </main>

      <footer className="footer">
        <span>Powered by Sleeper API + FantasyPros + ESPN · Auto-refresh 15min · v2.0</span>
      </footer>
      <LeagueIntelAssistant api={API} league="dynasty" enabled={Boolean(isLeagueMember)} />
      <SiteTelemetry api={API} league="dynasty" enabled={!currentUser?.guest} />
    </div>
  );
}

// ── AppInner — handles non-routing state, wraps with <Router> ─────────────────
function AppInner() {
  const { currentUser, loadingUser } = useUser();
  const [status, setStatus] = useState(null);
  const [posting, setPosting] = useState(false);
  const [postMsg, setPostMsg] = useState(null);
  const [showDynasty, setShowDynasty] = useState(
    localStorage.getItem('preferred_league') !== 'lor'
  );
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('theme') || 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else {
      root.removeAttribute('data-theme');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'));

  const triggerHeaders = () => {
    const pass = sessionStorage.getItem('hq.pass.v1');
    if (!pass) return null;
    const idempotencyKey = globalThis.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return {
      'X-HQ-Pass': pass,
      'X-Idempotency-Key': idempotencyKey,
    };
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const r = await fetch(`${API}/api/status`);
        setStatus(await r.json());
      } catch {}
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 60000);
    return () => clearInterval(t);
  }, []);

  const refresh = async () => {
    const headers = triggerHeaders();
    if (!headers) {
      setPostMsg({ type: 'error', text: 'Authorize in Commissioner HQ first' });
      return;
    }
    await fetch(`${API}/api/refresh`, { method: 'POST', headers });
    setTimeout(() => window.location.reload(), 3000);
  };

  const postDiscord = async () => {
    setPosting(true);
    try {
      const headers = triggerHeaders();
      if (!headers) {
        setPostMsg({ type: 'error', text: 'Authorize in Commissioner HQ first' });
        setPosting(false);
        return;
      }
      const r = await fetch(`${API}/api/discord/post`, { method: 'POST', headers });
      const d = await r.json();
      setPostMsg(d.success ? { type: 'success', text: 'Posted!' } : { type: 'error', text: 'Failed' });
    } catch { setPostMsg({ type: 'error', text: 'Error' }); }
    setPosting(false);
    setTimeout(() => setPostMsg(null), 4000);
  };

  if (loadingUser) return null;
  if (!currentUser) return <LoginScreen />;

  return (
    <Router>
      <Routes>
        <Route path="/league-of-record/*" element={<LORAppShell />} />
        <Route
          path="/*"
          element={
            <>
              {!showDynasty && (
                <HomeSelector onSelect={(l) => { if (l === 'dynasty') setShowDynasty(true); }} />
              )}
              {showDynasty && (
                <AppShell
                  status={status}
                  posting={posting}
                  postMsg={postMsg}
                  onRefresh={refresh}
                  onPostDiscord={postDiscord}
                  onToggleTheme={toggleTheme}
                  theme={theme}
                />
              )}
            </>
          }
        />
      </Routes>
    </Router>
  );
}

export default function App() {
  return <UserProvider><AppInner /></UserProvider>;
}
